import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { Bot, Target, RefreshCw, Users, Rocket, MapPin, Smartphone, Building2, Activity, Lightbulb, BookOpen, MessageSquare, Handshake } from 'lucide-react';
import { IconCard } from './IconCard';
import { isFinnishPage } from '../../lib/blogPaths';

// Homepage: brief, 1 sentence each (breadth)
const HOME_VALUE_PROPS = [
  { icon: Bot, title: 'AI-Enhanced by Default', description: '100% of developers use AI tools daily.' },
  { icon: RefreshCw, title: 'Seamless Integration', description: 'We become part of your team.' },
  { icon: Target, title: 'Proven Track Record', description: 'Since 2018, 100+ projects delivered.' },
];

const DEFAULT_VALUE_PROPS = HOME_VALUE_PROPS;

const ENGAGEMENT_ITEMS = [
  {
    icon: Users,
    title: 'Team Augmentation',
    description:
      'Add 1–5 specialists to your existing team. Our developers integrate seamlessly and work as part of your organization.',
    bestFor: 'Ongoing projects, specific expertise gaps',
  },
  {
    icon: Rocket,
    title: 'Dedicated Team',
    description:
      'A full Identio team working exclusively on your project. We handle everything from design to deployment.',
    bestFor: 'New products, major initiatives',
  },
  {
    icon: Target,
    title: 'Project-Based',
    description:
      'Fixed-scope projects delivered turnkey. From concept to launch, we own the delivery.',
    bestFor: 'Defined projects, specific deliverables',
  },
];

// Services page: "Why Leading Organizations Choose Identio" – substantive value props (from design)
const WHY_IDENTIO_ITEMS = [
  {
    icon: Smartphone,
    title: 'User-Friendly Software Solutions',
    description:
      'We always ensure that our services are accessible to everyone and strive to enhance both the customer experience and user-friendliness.',
  },
  {
    icon: Building2,
    title: 'Functional and Sustainable Architecture Solutions',
    description:
      'Sustainable architecture solutions, along with long-term planning, take your future needs into account.',
  },
  {
    icon: Activity,
    title: 'Scalability and Cost-Effectiveness',
    description:
      'Automating processes and improving efficiency help lower costs while ensuring your systems can scale to meet future needs.',
  },
  {
    icon: RefreshCw,
    title: 'Digital Transformation',
    description:
      'Upgrading and modernizing systems, as well as developing new service solutions to support your business.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation and Competitiveness',
    description:
      'Business insight enables the development of innovative services that enhance competitiveness.',
  },
];

const WHY_IDENTIO_ITEMS_FI = [
  {
    icon: Smartphone,
    title: 'Käyttäjäystävälliset ohjelmistot',
    description:
      'Huomioimme aina, että palvelumme ovat saavutettavia kaikille ja pyrimme parantamaan asiakaskokemusta sekä käyttäjäystävällisyyttä.',
  },
  {
    icon: Building2,
    title: 'Toimivat ja kestävät arkkitehtuuriratkaisut',
    description:
      'Kestävät arkkitehtuuriratkaisut sekä pitkän aikavälin suunnittelu huomioivat tulevaisuuden tarpeenne.',
  },
  {
    icon: Activity,
    title: 'Skaalautuvuus ja kustannustehokkuus',
    description:
      'Prosessien automatisointi ja tehokkuuden parantaminen auttavat vähentämään kustannuksia.',
  },
  {
    icon: RefreshCw,
    title: 'Digitaalinen transformaatio',
    description:
      'Järjestelmien päivitys ja modernisointi sekä uusien palvelukokonaisuuksien kehittäminen liiketoimintanne tueksi.',
  },
  {
    icon: Lightbulb,
    title: 'Innovaatio ja kilpailukyky',
    description:
      'Liiketoiminnallinen ymmärrys mahdollistaa innovatiivisten ja kilpailukykyä parantavien palvelujen kehittämisen.',
  },
];

// About page: "Key factors" – from identio.fi/en/about-us/
export const ABOUT_DIFFERENT_ITEMS = [
  {
    icon: Users,
    title: 'Quality & agility',
    description:
      'Our consultants have versatile experience in different development teams and multi-vendor environments. In our work, we use agile methods and the wide-ranging and comprehensive expertise of our team.',
  },
  {
    icon: BookOpen,
    title: 'Seniority and mentoring',
    description:
      'We have seniority and versatile expertise in our team. We are building a culture that puts developing our skills at the center and we constantly invest in learning. Our internal mentoring culture also encourages developing together.',
  },
  {
    icon: MessageSquare,
    title: 'Focused on reliability and transparency',
    description:
      'We take care of people regardless of on which side of the table they are seated. We want to be our client\'s most trustworthy partner. We ensure it by taking good care of our employees and by constantly developing our company and its culture.',
  },
  {
    icon: Handshake,
    title: 'Employee ownership',
    description:
      'Identio is owned by its people. We offer each of our employees the opportunity for ownership. For us, ownership means trust in the future and working together, as well as commitment and bearing responsibility to achieve common goals.',
  },
];

const WHY_IDENTIO_TITLE_EN = 'Why Leading Organizations Choose Identio';
const WHY_IDENTIO_TITLE_FI = 'Miksi asiakkaamme valitsevat Idention';
const QUALITY_TITLE_EN = 'Quality and industry best practices';
const QUALITY_TITLE_FI = 'Laatua ja alan parhaita käytäntöjä';

export function ValuePropsSection({
  title = WHY_IDENTIO_TITLE_EN,
  subtitle,
  items = DEFAULT_VALUE_PROPS,
  columns = 3,
}) {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isFinnish = isFinnishPage(router?.pathname);
  let resolvedTitle = title;
  if (isFinnish && title === WHY_IDENTIO_TITLE_EN) resolvedTitle = WHY_IDENTIO_TITLE_FI;
  else if (isFinnish && title === QUALITY_TITLE_EN) resolvedTitle = QUALITY_TITLE_FI;
  const resolvedItems =
    isFinnish && (title === WHY_IDENTIO_TITLE_EN || title === QUALITY_TITLE_EN) && items.length === 5
      ? WHY_IDENTIO_ITEMS_FI
      : items;
  const isWhyIdentio = (resolvedTitle === WHY_IDENTIO_TITLE_EN || resolvedTitle === WHY_IDENTIO_TITLE_FI) && resolvedItems.length > 3;
  const isFourItems = columns === 3 && items.length === 4;
  const gridClass = isWhyIdentio
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
    : columns === 2
      ? 'grid grid-cols-1 md:grid-cols-2 gap-8'
      : isFourItems
        ? 'value-props-grid grid grid-cols-1 md:grid-cols-2 gap-[40px] md:gap-10'
        : 'value-props-grid grid grid-cols-1 md:grid-cols-3 gap-[40px] md:gap-10';

  // When 5 items in 3-column layout: first 3 in grid, last 2 in centered 50/50 row
  const splitFive = columns === 3 && resolvedItems.length === 5;
  const firstRow = splitFive ? resolvedItems.slice(0, 3) : resolvedItems;
  const lastRow = splitFive ? resolvedItems.slice(3, 5) : [];

  const renderCard = (prop, index, baseDelay = 0) => {
    const Icon = prop.icon;
    return (
      <motion.div
        key={prop.title}
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: (baseDelay + index) * 0.1 }}
        className="value-prop-card flex flex-col bg-white p-6 sm:p-8 md:p-10 border border-gray-200 rounded-xl shadow-sm hover:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.08)] hover:border-gray-300 hover:-translate-y-0.5 transition-all duration-300 group min-w-0"
      >
        <div className="flex justify-center mb-6">
          <IconCard Icon={Icon} />
        </div>
        <h3 className={`mb-3 ${isWhyIdentio ? 'text-lg font-semibold text-gray-900' : 'heading-3'}`}>{prop.title}</h3>
        <p className="text-base text-gray-500 leading-relaxed flex-1">{prop.description}</p>
        {prop.bestFor && (
          <p className="text-sm text-gray-500 font-medium mt-4 pt-4 border-t border-gray-100">
            Best for: {prop.bestFor}
          </p>
        )}
      </motion.div>
    );
  };

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="heading-2 mb-4">{resolvedTitle}</h2>
          {subtitle && (
            <p className="text-large text-[#6b7280] max-w-2xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        <div className={`${gridClass} min-w-0`}>
          {firstRow.map((prop, index) => renderCard(prop, index, 0))}
        </div>

        {lastRow.length > 0 && (
          <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {lastRow.map((prop, index) => renderCard(prop, index, 3))}
          </div>
        )}
      </div>
    </section>
  );
}

export { ENGAGEMENT_ITEMS, WHY_IDENTIO_ITEMS, WHY_IDENTIO_ITEMS_FI };
